/**
 * Helper pour formater les nombres avec espace pour les milliers et point pour les décimales
 * Format: 1 311.8 au lieu de 1,311.8
 * Format: 2 000 au lieu de 2000
 * 
 * @param value - Le nombre à formater
 * @param decimals - Nombre de décimales (par défaut: 0)
 * @returns Le nombre formaté avec espace pour les milliers
 * 
 * @example
 * formatNumber(1311.8, 1) // "1 311.8"
 * formatNumber(2000) // "2 000"
 * formatNumber(1234567.89, 2) // "1 234 567.89"
 */
export const formatNumber = (value: number | null | undefined, decimals: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  // Arrondir à la précision demandée
  const rounded = decimals > 0 
    ? Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
    : Math.round(value);

  // Séparer la partie entière et décimale
  const parts = rounded.toString().split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Ajouter des espaces tous les 3 chiffres pour la partie entière
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Reconstruire avec la partie décimale si nécessaire
  if (decimals > 0 && decimalPart) {
    // Compléter avec des zéros si nécessaire
    const paddedDecimal = decimalPart.padEnd(decimals, '0').slice(0, decimals);
    return `${formattedInteger}.${paddedDecimal}`;
  } else if (decimals > 0 && !decimalPart) {
    // Ajouter des zéros si on veut des décimales mais qu'il n'y en a pas
    return `${formattedInteger}.${'0'.repeat(decimals)}`;
  }

  return formattedInteger;
};

/**
 * Helper pour formater les montants en FCFA
 * 
 * @param value - Le montant à formater
 * @param decimals - Nombre de décimales (par défaut: 0)
 * @returns Le montant formaté avec " FCFA" à la fin
 * 
 * @example
 * formatCurrency(1311.8, 1) // "1 311.8 FCFA"
 * formatCurrency(2000) // "2 000 FCFA"
 */
export const formatCurrency = (value: number | null | undefined, decimals: number = 0): string => {
  return `${formatNumber(value, decimals)} FCFA`;
};

